"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { CreateDraft, Product, Project, VideoStyle } from "@easyshot/shared";
import { PLATFORM_PRESETS, STYLE_OPTIONS } from "@easyshot/shared";
import { readProjectHistory } from "@/lib/project-history";
import { draftStepLabel } from "@/lib/guided-ugc";
import { Badge, Card, LinkButton, Spinner } from "./ui";
import { ProjectCover } from "./project-cover";

function styleLabel(style: string): string {
  if (style === "minimax_h3_product_video") return "MiniMax H3 Product Video";
  if (style === "infinitetalk_avatar" || style === "clone_creator") return "Spokesperson";
  return STYLE_OPTIONS.find((option) => option.id === style)?.label ?? style;
}

export function ProjectList() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [drafts, setDrafts] = useState<CreateDraft[] | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    let active = false;
    const message = (error: unknown) => error instanceof Error ? error.message : "Could not load history. Please retry.";
    const load = async () => {
      await Promise.all([
        readProjectHistory<Project>("/api/projects", controller.signal).then(value => {
          if (!alive) return;
          active = value.some(p => p.jobs.some(j => j.status !== "succeeded" && j.status !== "failed"));
          setProjects(value); setProjectError(null);
        }).catch(error => { if (alive) setProjectError(message(error)); }),
        readProjectHistory<CreateDraft>("/api/drafts", controller.signal).then(value => {
          if (alive) { setDrafts(value); setDraftError(null); }
        }).catch(error => { if (alive) setDraftError(message(error)); }),
      ]);
      if (alive) timer = setTimeout(() => void load(), active ? 3000 : 15000);
    };
    void load();
    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [retry]);

  const filtered = projects?.filter(p => {
    const matches = filter === "all" || p.jobs.some(j => filter === "ready" ? j.status === "succeeded" : filter === "failed" ? j.status === "failed" : j.status !== "succeeded" && j.status !== "failed");
    return matches && `${p.name} ${p.id} ${new Date(p.createdAt).toLocaleString()}`.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="mt-2 text-sm text-muted">
            Drafts, renders in progress, failed attempts, and completed videos — all in one place.
          </p>
        </div>
        <LinkButton href="/studio">+ New</LinkButton>
      </div>

      {(projectError || draftError) && <div role="alert" className="mb-5 rounded-xl border border-red-400/40 p-4 text-sm">
        {projectError && <p>Video history: {projectError}</p>}
        {draftError && <p>Drafts: {draftError}</p>}
        <button type="button" className="mt-2 underline" onClick={() => setRetry(n => n + 1)}>Retry loading history</button>
      </div>}
      {projects === null && !projectError && (
        <div className="grid place-items-center py-20 text-muted">
          <Spinner className="size-6" />
        </div>
      )}

      {!projectError && !draftError && projects?.length === 0 && drafts?.length === 0 && (
        <Card className="px-6 py-20 text-center">
          <div className="text-4xl">🎬</div>
          <h2 className="mt-4 text-lg font-semibold">No projects yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
            Paste a product link and see your first script and storyboard in 30
            seconds.
          </p>
          <LinkButton href="/studio" className="mt-6">
            Create your first project
          </LinkButton>
        </Card>
      )}

      <div className="flex flex-col">
      {drafts && drafts.length > 0 && (
        <section className="order-2 mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Drafts</h2>
            <span className="text-xs text-muted">{drafts.length} saved</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {drafts.map((draft) => {
              const payload = draft.payload as {
                product?: Product | null;
                style?: VideoStyle;
                ugcMode?: string;
              };
              const image = payload.product?.images[0];
              return (
                <Link
                  key={draft.id}
                  href={`/studio?draft=${encodeURIComponent(draft.id)}`}
                >
                  <Card className="flex h-full gap-4 border-[var(--accent)]/30 p-4 transition hover:border-[var(--accent)]/60 hover:bg-white/[0.06]">
                    {image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={image}
                        alt=""
                        className="size-20 shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="grid size-20 shrink-0 place-items-center rounded-xl bg-white/[0.05] text-2xl">
                        📝
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="truncate text-sm font-medium">{draft.name}</div>
                        <Badge tone="accent">Draft</Badge>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {payload.style && (
                          <Badge>
                            {payload.ugcMode === "product" ? "Product Ad · draft only" : payload.ugcMode === "talking" ? "Talking Video" : styleLabel(payload.style)}
                          </Badge>
                        )}
                        <Badge>{draftStepLabel(draft.step, draft.payload)}</Badge>
                      </div>
                      <div className="mt-3 text-[12px] text-muted">
                        Saved {new Date(draft.updatedAt).toLocaleString()}
                      </div>
                      <div className="mt-2 text-[12px] font-medium text-purple-300">
                        Continue Create →
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="order-1">
      {projects && projects.length > 0 && (
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Video history</h2>
          <span className="text-xs text-muted">{projects.length} projects</span>
        </div>
      )}
      {projects && projects.length > 0 && <div className="mb-4 flex flex-wrap gap-3">
        <input aria-label="Search video history" placeholder="Search name, date or project ID" value={search} onChange={e => { setSearch(e.target.value); setVisibleCount(12); }} className="min-w-0 flex-1 rounded-xl border border-white/20 bg-black/20 px-3 py-2 text-sm" />
        <select aria-label="Filter video history" value={filter} onChange={e => { setFilter(e.target.value); setVisibleCount(12); }} className="rounded-xl border border-white/20 bg-black px-3 py-2 text-sm">
          <option value="all">All videos</option><option value="ready">Ready</option><option value="running">In progress</option><option value="failed">Failed</option>
        </select>
      </div>}

      <div className="grid gap-4 sm:grid-cols-2">
        {filtered?.slice(0, visibleCount).map((p) => {
          const done = p.jobs.filter((j) => j.status === "succeeded").length;
          const failed = p.jobs.filter((j) => j.status === "failed").length;
          const running = p.jobs.filter(
            (j) => j.status !== "succeeded" && j.status !== "failed",
          ).length;
          return (
            <Link key={p.id} href={`/studio/projects/${p.id}`}>
              <Card className="flex h-full gap-4 p-4 transition hover:border-[var(--border-strong)] hover:bg-white/[0.06]">
                <div className="shrink-0 text-center">
                  <ProjectCover project={p} className="size-24" />
                  <div className="mt-1 text-[10px] text-muted">Video cover</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="mt-1 text-xs text-muted">{new Date(p.createdAt).toLocaleString()}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {p.product.backgroundMedia && <Badge tone="accent">Custom background</Badge>}
                    <Badge>
                      {styleLabel(p.style)}
                    </Badge>
                    {p.platforms.slice(0, 2).map((pl) => (
                      <Badge key={pl}>
                        {PLATFORM_PRESETS.find((x) => x.id === pl)?.label}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[12px] text-muted">
                    <span>{p.jobs.length} videos</span>
                    {running > 0 && (
                      <span className="flex items-center gap-1.5 text-[var(--warning)]">
                        <Spinner className="size-3 border-[var(--warning)]/30 border-t-[var(--warning)]" />
                        {running} rendering
                      </span>
                    )}
                    {running === 0 && done > 0 && (
                      <span className="text-[var(--success)]">
                        {done} ready
                      </span>
                    )}
                    {failed > 0 && (
                      <span className="text-[var(--danger)]">
                        {failed} failed
                      </span>
                    )}
                    {p.jobs.length === 0 && (
                      <span className="text-[var(--warning)]">Not generated</span>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
      {filtered?.length === 0 && !!projects?.length && <p className="py-6 text-sm text-muted">No matching projects. Try another search or filter.</p>}
      {filtered && filtered.length > visibleCount && <button type="button" className="mt-5 rounded-xl border border-white/20 px-5 py-3 text-sm" onClick={() => setVisibleCount(n => n + 12)}>Show more history ({filtered.length - visibleCount} more)</button>}
      </section>
      </div>
    </div>
  );
}
